import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQueryNav } from '../lib/nav';
import { Header } from '../components/Header';
import { useAdmin } from '../context/useAdmin';
import { formatCreated, statusColor } from '../screens/wells/wells';
import { WORLD_LAND_PATH } from '../screens/wells/worldPath';
import type { AdminWell } from '../lib/access';
import type { Well } from '../lib/types';

type View = 'grid' | 'list' | 'map';

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? '2.1.0.15';

/** Equirectangular grid the land path was projected onto. */
const MAP_W = 1440;
const MAP_H = 720;
const toX = (lon: number) => ((lon + 180) / 360) * MAP_W;
const toY = (lat: number) => ((90 - lat) / 180) * MAP_H;

const DEFAULT_VIEW: View = 'list';

function normaliseView(raw: string | null): View {
  return raw === 'grid' || raw === 'map' ? raw : DEFAULT_VIEW;
}

export function MyWells() {
  // Grid, list or map survives a refresh, so this sits in the URL too. The
  // default is left out of the query string rather than spelled out in it.
  const { params, set } = useQueryNav();
  const view = normaliseView(params.get('view'));
  const setView = (next: View) => set({ view: next === DEFAULT_VIEW ? null : next });

  const navigate = useNavigate();
  const open = (w: Well) => navigate(`/web?wellId=${w.id}`);

  // The list is the database's, narrowed to the wells this person was given.
  const admin = useAdmin();
  const error = admin.wellsError;
  const managesPeopleOnly = admin.access.role?.scope === 'none';

  // A superadmin has no wells of their own: they come here through a company
  // (pages/CompaniesHome), and see that company's wells. "none" is the wells
  // nobody has filed under a company yet.
  const acrossCompanies = admin.access.kind === 'superadmin';
  const asked = params.get('company');
  const company = admin.store.companies.find((c) => String(c.id) === asked);
  const unfiled = asked === 'none';

  // Not before the companies have been read: until then every company is
  // unknown, and a reload on one company's wells would land on the list.
  if (acrossCompanies && !company && !unfiled && (!asked || admin.storeLoaded)) {
    return <Navigate to="/web/Companies" replace />;
  }

  const known = new Set(admin.store.companies.map((c) => c.id));
  const inScope = (w: AdminWell) => {
    if (!acrossCompanies) return true;
    if (unfiled) return w.companyId == null || !known.has(w.companyId);
    return w.companyId === company?.id;
  };
  const wells: Well[] | null = admin.wellsLoaded ? admin.wells.filter(inScope) : null;
  const title = !acrossCompanies ? 'My Wells' : (company?.name ?? 'Wells without a company');

  return (
    <div className="app">
      <Header context="wells" />

      <main className="wellspage">
        <header className="wellspage-head">
          <div>
            {acrossCompanies && (
              <Link className="wellspage-crumb" to="/web/Companies">
                ‹ Companies
              </Link>
            )}
            <h1>{title}</h1>
          </div>

          <div className="viewas">
            <span>View As</span>
            <ViewBtn current={view} value="grid" onSelect={setView} label="Grid View">
              <path
                d="M2 2h4v4H2zM8 2h4v4H8zM14 2h4v4h-4zM2 8h4v4H2zM8 8h4v4H8zM14 8h4v4h-4zM2 14h4v4H2zM8 14h4v4H8zM14 14h4v4h-4z"
                fill="currentColor"
              />
            </ViewBtn>
            <ViewBtn current={view} value="list" onSelect={setView} label="List View">
              <path
                d="M2 4h16M2 8h16M2 12h16M2 16h16"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </ViewBtn>
            <ViewBtn current={view} value="map" onSelect={setView} label="Map View">
              <path
                d="M10 1.6c3 0 5.4 2.4 5.4 5.4 0 3.9-5.4 11.4-5.4 11.4S4.6 10.9 4.6 7c0-3 2.4-5.4 5.4-5.4Z"
                fill="currentColor"
              />
              <circle cx="10" cy="7" r="2" fill="#fff" />
            </ViewBtn>
          </div>
        </header>

        {error && (
          <div className="alert alert--page" role="alert">
            {error}
          </div>
        )}

        {wells === null && <p className="content-loading">Loading wells…</p>}

        {wells !== null && wells.length === 0 && !error && (
          <p className="content-loading">
            {managesPeopleOnly ? (
              <>
                Your role looks after people, not wells. <Link to="/web/Admin">Open Administration</Link>
              </>
            ) : acrossCompanies ? (
              <>
                {unfiled ? 'Every well is filed under a company.' : `${title} has no wells yet.`}{' '}
                <Link to="/web/Admin/Wells">Open the Wells page</Link>
              </>
            ) : (
              'No wells yet.'
            )}
          </p>
        )}

        {wells !== null && wells.length > 0 && (
          <>
            {view === 'list' && <ListView wells={wells} onOpen={open} />}
            {view === 'grid' && <GridView wells={wells} onOpen={open} />}
            {view === 'map' && <MapView wells={wells} onOpen={open} />}
          </>
        )}
      </main>
    </div>
  );
}

function ViewBtn({
  current,
  value,
  onSelect,
  label,
  children,
}: {
  current: View;
  value: View;
  onSelect: (v: View) => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={current === value ? 'viewas-btn is-active' : 'viewas-btn'}
      title={label}
      aria-label={label}
      aria-pressed={current === value}
      onClick={() => onSelect(value)}
    >
      <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true">
        {children}
      </svg>
    </button>
  );
}

function ListView({ wells, onOpen }: { wells: Well[]; onOpen: (w: Well) => void }) {
  return (
    <div className="wellstable-wrap">
      <table className="wellstable">
        <thead>
          <tr>
            <th>Date Created</th>
            <th>Well Name</th>
            <th>Field Name</th>
            <th>Country</th>
            <th>Rig Name</th>
            <th>Purpose</th>
            <th>Operational Status</th>
          </tr>
        </thead>
        <tbody>
          {wells.map((w) => (
            <tr key={w.id} onClick={() => onOpen(w)} tabIndex={0}>
              <td>{formatCreated(w.createdAt)}</td>
              <td className="wellstable-name">{w.name}</td>
              <td>{w.field}</td>
              <td>{w.country}</td>
              <td>{w.rig}</td>
              <td>{w.purpose}</td>
              <td>{w.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GridView({ wells, onOpen }: { wells: Well[]; onOpen: (w: Well) => void }) {
  return (
    <div className="wellsgrid">
      {wells.map((w) => (
        <button type="button" className="wellcard" key={w.id} onClick={() => onOpen(w)}>
          <span className="wellcard-status" style={{ background: statusColor(w.status) }} />
          <strong>{w.name}</strong>
          <span className="wellcard-field">{w.field || '—'}</span>
          <dl>
            <div>
              <dt>Country</dt>
              <dd>{w.country}</dd>
            </div>
            <div>
              <dt>Rig</dt>
              <dd>{w.rig}</dd>
            </div>
            <div>
              <dt>Purpose</dt>
              <dd>{w.purpose}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{w.status || '—'}</dd>
            </div>
          </dl>
        </button>
      ))}
    </div>
  );
}

function MapView({ wells, onOpen }: { wells: Well[]; onOpen: (w: Well) => void }) {
  const [zoom, setZoom] = useState(1);
  const [hover, setHover] = useState<{ lon: number; lat: number } | null>(null);

  function track(e: React.MouseEvent<SVGSVGElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * MAP_W;
    const py = ((e.clientY - r.top) / r.height) * MAP_H;
    setHover({ lon: (px / MAP_W) * 360 - 180, lat: 90 - (py / MAP_H) * 180 });
  }

  return (
    <div className="wellsmap">
      <div className="wellsmap-canvas">
        <svg
          viewBox={`0 0 ${MAP_W} ${MAP_H}`}
          role="img"
          aria-label="World map of wells"
          onMouseMove={track}
          onMouseLeave={() => setHover(null)}
          style={{ transform: `scale(${zoom})` }}
        >
          <rect width={MAP_W} height={MAP_H} fill="#7fb2d9" />
          <path d={WORLD_LAND_PATH} fill="#f2e9c9" fillRule="evenodd" />

          {wells.map((w) => (
            <g key={w.id} className="wellsmap-pin" onClick={() => onOpen(w)}>
              <circle
                cx={toX(w.longitude ?? 0)}
                cy={toY(w.latitude ?? 0)}
                r="7"
                fill={statusColor(w.status)}
                stroke="#ffffff"
                strokeWidth="2"
              />
              <title>
                {w.name} — {w.status || 'No status'}
              </title>
            </g>
          ))}
        </svg>

        <div className="wellsmap-zoom">
          <button type="button" onClick={() => setZoom((z) => Math.min(4, z * 1.4))} aria-label="Zoom in">
            +
          </button>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(1, z / 1.4))}
            aria-label="Zoom out"
          >
            −
          </button>
        </div>

        <button type="button" className="wellsmap-settings">
          <span>Settings</span>
        </button>

        <span className="wellsmap-scale">2000 km</span>
        <span className="wellsmap-version">Version: {APP_VERSION}</span>
      </div>

      <div className="wellsmap-status">
        {hover
          ? `Latitude: ${hover.lat.toFixed(4)}°, Longitude: ${hover.lon.toFixed(4)}°`
          : 'Latitude: —, Longitude: —'}
      </div>
    </div>
  );
}
